<template>
	<div
		class="scrollbar-hide -mx-[20px] flex items-center gap-16px overflow-x-auto border-b border-n-200 px-20px mdp:-mx-[32px] mdp:px-[52px] lg:mx-0 lg:px-[20px]"
	>
		<a
			v-for="tab in visibleTabs"
			:key="tab.value"
			:href="getCouponCenterUrl(tab.url)"
			:class="[
				'typography-s1-emp whitespace-nowrap rounded-[1px] px-8px pt-16px transition-colors duration-200 hover:text-n-950 focus:text-n-950',
				activeTab === tab.value
					? 'border-b-[3px] border-o-500 pb-[13px] text-n-950'
					: 'border-b border-transparent pb-[15px] text-n-500 hover:border-n-950',
			]"
			@click.prevent="handleTabClick(tab)"
		>
			{{ tab.name }}
		</a>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { CouponCenterTabsType } from '@/assets/ts/pages/coupons'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'

export interface CouponTab {
	name: string
	value: CouponCenterTabsType
	url: string
}

const props = defineProps<{
	visibleTabs: CouponTab[]
	activeTab: CouponCenterTabsType
}>()

const emit = defineEmits<{
	(e: 'update:active-tab', value: CouponCenterTabsType): void
}>()

const activeTab = ref<CouponCenterTabsType>(props.activeTab)

const scrollToActiveTab = () => {
	const targetUrl = props.visibleTabs.find(tab => tab.value === activeTab.value)
		?.url
	if (!targetUrl) return

	const activeTabElement = document.querySelector(
		`[href="${getCouponCenterUrl(targetUrl)}"]`,
	)
	activeTabElement?.scrollIntoView({
		behavior: 'smooth',
		block: 'nearest',
		inline: 'center',
	})
}

const handleTabClick = (tab: CouponTab) => {
	activeTab.value = tab.value
	scrollToActiveTab()
	emit('update:active-tab', tab.value)

	const isProd = import.meta.env.PROD
	const url = new URL(window.location.href)
	if (isProd) {
		const newPath = `${getSiteBaseUrl()}/coupons/${tab.url}/`
		window.history.pushState({ tab: tab.url }, '', newPath)
	} else {
		url.searchParams.set('tab', tab.url)
		window.history.pushState({ tab: tab.url }, '', url.toString())
	}
}

const handlePopState = (event: PopStateEvent) => {
	if (event.state?.tab) {
		activeTab.value = event.state.tab as CouponCenterTabsType
		scrollToActiveTab()
	}
}

const getCouponCenterUrl = (tabUrl: string) => {
	const isProd = import.meta.env.PROD
	const baseUrl = isProd ? `${getSiteBaseUrl()}/coupons` : '/coupons'
	return isProd ? `${baseUrl}/${tabUrl}/` : `${baseUrl}?tab=${tabUrl}`
}

onMounted(() => {
	const isProd = import.meta.env.PROD
	let tabParam: CouponCenterTabsType | null

	if (isProd) {
		const pathParts = window.location.pathname.split('/').filter(Boolean)
		tabParam = pathParts[
			pathParts.indexOf('coupons') + 1
		] as CouponCenterTabsType
	} else {
		const url = new URL(window.location.href)
		tabParam = url.searchParams.get('tab') as CouponCenterTabsType | null
	}

	const tabKey = props.visibleTabs.find(tab => tab.url === tabParam)?.value
	if (tabKey) {
		activeTab.value = tabKey
		emit('update:active-tab', tabKey)
		scrollToActiveTab()
	} else {
		activeTab.value = CouponCenterTabsType.All
		emit('update:active-tab', CouponCenterTabsType.All)
		scrollToActiveTab()
	}

	window.addEventListener('popstate', handlePopState)
})

onUnmounted(() => {
	window.removeEventListener('popstate', handlePopState)
})

watch(
	() => props.activeTab,
	(newValue: CouponCenterTabsType) => {
		activeTab.value = newValue
	},
)
</script>
